import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import PayPalCheckout from "@/components/PayPalCheckout";
import { useCurrency } from "@/context/CurrencyContext";
import {
  CreditCard,
  Shield,
  Lock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get("booking_id");
  const { formatMoney, formatXCD, isXCD } = useCurrency();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
    } else {
      setError("No booking ID provided");
      setLoading(false);
    }
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
      if (response.data.status === "paid") {
        setError("This booking has already been paid");
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const applyPromo = async () => {
    setPromoError("");
    if (!promoCode.trim()) return;
    try {
      const { data } = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim().toUpperCase(),
        service: "callout_fee",
        amount: booking?.callout_fee || 0,
      });
      if (data.valid) {
        setPromo(data);
        toast.success("Promo code applied!");
      } else {
        setPromo(null);
        setPromoError(data.error || "Invalid code");
      }
    } catch (e) {
      setPromo(null);
      setPromoError("Could not validate that code.");
    }
  };

  const handleStripePayment = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/stripe/checkout`, {
        booking_id: bookingId,
        payment_method: "stripe",
        origin_url: window.location.origin,
        promo_code: promo ? promoCode.trim().toUpperCase() : null,
      });

      if (response.data.free) {
        navigate(`/payment/success?free=1`);
        return;
      }
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to create payment session");
      }
    } catch (err) {
      console.error("Stripe payment error:", err);
      toast.error(err.response?.data?.detail || "Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const createPayPalOrder = (data, actions) => {
    // Client-side order creation using PayPal SDK.
    // Backend transaction is recorded on approval (onPayPalApprove).
    const amount = Number(booking?.callout_fee || 0).toFixed(2);
    return actions.order.create({
      purchase_units: [
        {
          description: `ProToCall - ${booking?.service_name || "Service"}`,
          custom_id: bookingId,
          amount: {
            value: amount,
            currency_code: "USD",
          },
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
        user_action: "PAY_NOW",
      },
    });
  };

  const onPayPalApprove = async (data, actions) => {
    try {
      // Capture the payment client-side using PayPal SDK
      const details = await actions.order.capture();
      // Record the transaction on the backend
      await axios.post(`${API}/payments/paypal/record`, {
        order_id: data.orderID,
        booking_id: bookingId,
        amount: Number(booking?.callout_fee || 0),
        payer_email: details?.payer?.email_address || null,
      });
      toast.success("Payment successful!");
      navigate(`/payment/success?session_id=${data.orderID}&method=paypal`);
    } catch (err) {
      console.error("PayPal capture error:", err);
      toast.error("Payment capture failed. Please contact support if charged.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-20">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Error</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button
            onClick={() => navigate("/booking")}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Booking
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 md:py-20" data-testid="payment-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-orange-100 text-orange-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            Secure Payment
          </span>
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="payment-page-title"
          >
            Complete Your Payment
          </h1>
          <p className="text-slate-600">
            Choose your preferred payment method to complete your booking
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Payment Methods */}
          <div className="lg:col-span-2">
            <Card className="bg-white border border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle
                  className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  <Lock className="w-5 h-5 text-green-500" />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={setPaymentMethod}
                  className="space-y-4"
                >
                  {/* Stripe Option */}
                  <div
                    className={`payment-option p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentMethod === "stripe"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => setPaymentMethod("stripe")}
                    data-testid="stripe-payment-option"
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="stripe" id="stripe" />
                      <Label
                        htmlFor="stripe"
                        className="flex items-center gap-3 cursor-pointer flex-1"
                      >
                        <div className="w-12 h-8 bg-slate-100 rounded flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            Credit / Debit Card
                          </p>
                          <p className="text-sm text-slate-500">
                            Visa, Mastercard, Amex via Stripe
                          </p>
                        </div>
                      </Label>
                    </div>
                  </div>

                  {/* PayPal Option */}
                  <div
                    className={`payment-option p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      paymentMethod === "paypal"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => setPaymentMethod("paypal")}
                    data-testid="paypal-payment-option"
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="paypal" id="paypal" />
                      <Label
                        htmlFor="paypal"
                        className="flex items-center gap-3 cursor-pointer flex-1"
                      >
                        <div className="w-12 h-8 bg-[#003087] rounded flex items-center justify-center">
                          <span className="text-white font-bold text-xs">PayPal</span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">PayPal</p>
                          <p className="text-sm text-slate-500">
                            Pay with your PayPal account
                          </p>
                        </div>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>

                {/* Payment Button */}
                <div className="mt-8">
                  {paymentMethod === "stripe" ? (
                    <Button
                      onClick={handleStripePayment}
                      disabled={processing}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-14 text-lg"
                      data-testid="pay-with-stripe-btn"
                    >
                      {processing ? (
                        <>
                          <span className="animate-spin mr-2">⌛</span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="mr-2 w-5 h-5" />
                          Pay {formatMoney(promo ? promo.final_amount : booking?.callout_fee)} with Card
                        </>
                      )}
                    </Button>
                  ) : (
                    <div data-testid="paypal-buttons">
                      <PayPalCheckout
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onCancel={() => toast.info("PayPal payment cancelled")}
                        onError={() => toast.error("PayPal payment failed")}
                        amount={promo ? promo.final_amount : booking?.callout_fee}
                        currency="USD"
                      />
                    </div>
                  )}
                </div>

                {/* Security Info */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Shield className="w-4 h-4 text-green-500" />
                    <span>Your payment information is secure and encrypted</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="bg-white border border-slate-200 shadow-lg sticky top-24">
              <CardHeader>
                <CardTitle
                  className="text-lg font-semibold text-slate-900"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Service</p>
                    <p className="font-medium text-slate-900">{booking?.service_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Date & Time</p>
                    <p className="font-medium text-slate-900">
                      {booking?.preferred_date} at {booking?.preferred_time}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Customer</p>
                    <p className="font-medium text-slate-900">{booking?.customer_name}</p>
                  </div>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="mb-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">Promo code</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                          placeholder="Enter code"
                          className="flex-1 h-10 px-3 rounded-lg border border-slate-300 uppercase text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                          data-testid="payment-promo-input"
                        />
                        <Button
                          type="button"
                          onClick={applyPromo}
                          variant="outline"
                          className="h-10 px-4 border-slate-300"
                          data-testid="payment-promo-apply"
                        >
                          Apply
                        </Button>
                      </div>
                      {promoError && <p className="text-xs text-red-600 mt-1" data-testid="payment-promo-error">{promoError}</p>}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="font-medium">{formatMoney(booking?.callout_fee)}</span>
                    </div>
                    {promo && (
                      <div className="flex justify-between items-center mt-2" data-testid="payment-promo-discount">
                        <span className="text-green-700">Discount ({promo.code})</span>
                        <span className="font-medium text-green-700">−{formatMoney(promo.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-slate-600">Tax</span>
                      <span className="font-medium">{formatMoney(0)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-200">
                      <span className="text-lg font-semibold text-slate-900">Total</span>
                      <span className="text-2xl font-bold text-orange-500" data-testid="payment-total">
                        {formatMoney(promo ? promo.final_amount : (booking?.callout_fee || 0))}
                      </span>
                    </div>
                    {!isXCD && (
                      <p className="text-xs text-slate-500 mt-2 text-right" data-testid="payment-xcd-note">
                        Billed in {formatXCD(promo ? promo.final_amount : (booking?.callout_fee || 0))} (our processing currency)
                      </p>
                    )}
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Satisfaction Guaranteed</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Licensed & Insured Professionals</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Secure Payment Processing</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
